import Inherit from '../../core/inherit';
import ShelfController from '../../core/shelf.controller';
import LoginDelegate from './login.delegate';

import { User } from '../../data';
import { setAuthCookie } from '../../utils/cookies';
import { homePathFor } from '../../core/access';
import { isCentralConfigured, refreshFeatureFlags, resolveCompany, takeEmailFromHash } from '../../data/central';
import { getActiveCompany, getBootCompanyId, legacyCompany } from '../../company/session';
import { reloadTo, switchCompany } from '../../company/reset';

/**
 * Email-first login for every company this admin serves. Step 'email' asks central-service
 * which company the email belongs to:
 *  - a company in the registry (company/companies.js): it becomes the session company and the
 *    password step continues here, against that company's API;
 *  - a company this admin does not serve: its users are sent to their own admin;
 *  - no answer (404, network error, central unset): Mary's classic login, as before.
 * Without REACT_APP_CENTRAL_URL the form starts at step 'password' (email + password together).
 *
 * The password only ever goes to one backend: the session company's.
 */
class LoginController extends Inherit(ShelfController, LoginDelegate) {
    async componentDidMount() {
        // Sent here by another company's admin (#email=...), or back from the forgot-password
        // page (router state): resolve the email here as well, so the password still goes to the
        // company central-service names for it.
        const email = takeEmailFromHash() || this.takeEmailFromRouterState();
        if (email) {
            if (isCentralConfigured) this.setState({ email }, () => this.continueWithEmail());
            else this.setState({ email, step: 'password' });
        }
        // Coming back (browser Back) to a page restored from the back/forward cache after a
        // redirect to another company's admin: re-enable the form.
        this._onPageShow = (e) => {
            if (e.persisted) this.setState({ disabled: false, redirectingTo: null });
        };
        window.addEventListener('pageshow', this._onPageShow);
        // A saved session the API can't confirm (offline, CORS, revoked) just shows the form.
        try {
            await super.componentDidMount();
        } catch (err) {
            console.warn('Session check failed, showing the sign-in form:', err?.message || err);
        }
    }

    /**
     * The email the forgot-password page passed in the router state, which is then dropped so a
     * reload or Back doesn't apply it again. Returns the email or ''.
     */
    takeEmailFromRouterState() {
        const { location, navigate } = this.props;
        const email = location?.state?.email;
        if (typeof email !== 'string' || !email.trim()) return '';
        // After mount: react-router ignores navigate() before its own effects have run.
        setTimeout(() => navigate(`${location.pathname}${location.search}`, { replace: true, state: null }), 0);
        return email.trim();
    }

    componentWillUnmount() {
        window.removeEventListener('pageshow', this._onPageShow);
    }

    async initialize() {
        if (this.isAuthenticated) {
            this.props.navigate(homePathFor(User.getInstance()));
        }
    }

    /** Form submit (button click or Enter in any field). */
    submit(e) {
        e?.preventDefault?.();
        if (this.state.disabled) return;
        if (this.state.step === 'email') this.continueWithEmail();
        else this.login();
    }

    /** Step 1: find the email's company. Any failure falls back to this panel's password step. */
    async continueWithEmail() {
        const { onMessage } = this.props;
        const email = (this.state.email || '').trim();
        if (!email) {
            onMessage('error', 'Ingresa tu correo electrónico');
            return;
        }

        this.setState({ disabled: true });
        const target = await resolveCompany(email);

        if (target.kind === 'redirect') {
            // Stay disabled while the browser leaves.
            this.setState({ redirectingTo: target.companyName || 'tu empresa' });
            window.location.assign(target.url);
            return;
        }
        if (target.kind === 'elsewhere') {
            this.setState({ disabled: false });
            onMessage('info', `Tu cuenta pertenece a ${target.companyName || 'otra empresa'}. Ingresa desde su panel administrativo.`);
            return;
        }
        // A company this admin serves, or the classic Mary login when central had no answer.
        const company = target.kind === 'company' ? target.company : legacyCompany();
        this.selectCompany(company);
        this.setState({ email, company, disabled: false, step: 'password' });
    }

    /**
     * Make `company` the session company. Switching from another company (or from a browser
     * with no company yet) first forgets everything the admin kept: cookie, storage, caches.
     */
    selectCompany(company) {
        switchCompany(company);
    }

    /** From the password step back to the email step. */
    changeEmail() {
        if (this.state.disabled) return;
        this.setState({ step: 'email', password: '', company: null });
    }

    async login() {
        const { onMessage } = this.props;

        if (!this.state.email || !this.state.password) {
            onMessage('error', 'Todos los campos son requeridos');
            return;
        }

        this.setState({ disabled: true });

        // The company resolved at the email step; the classic form (no central-service) is Mary.
        const company = this.state.company || legacyCompany();
        this.selectCompany(company);
        // This company's flags, fetched while the credentials are checked (never blocks the login)
        const flagsReady = refreshFeatureFlags(company.companyId, { force: true });

        try {
            const payload = {
                email: this.state.email,
                password: this.state.password,
            };

            const res = await this.data.requestLogin(payload);

            if (!res?.message && getActiveCompany().adminUserTypes.includes(res?.user.userType)) {
                User.Instance(res?.user);
                setAuthCookie(res?.token, res?.expiresIn);
                this.setState({
                    email: '',
                    password: '',
                    disabled: false,
                });
                await Promise.race([flagsReady, new Promise((resolve) => setTimeout(resolve, 1500))]);

                // The page was built for another company (router, menu, texts): load it fresh.
                if (company.companyId !== getBootCompanyId()) {
                    reloadTo(homePathFor(res?.user));
                    return;
                }
                this.props.navigate(homePathFor(res?.user));
                return;
            }

            onMessage('error', res?.message ?? 'Error al iniciar sesión');
        } catch (e) {
            const status = e?.response?.status;
            const data = e?.response?.data;
            const msg =
                (typeof data === 'object' && data?.error) ||
                (typeof data === 'object' && data?.message) ||
                (typeof data === 'string' ? data : null);

            if (status === 429) {
                onMessage(
                    'error',
                    msg || 'Demasiados intentos de inicio de sesión. Espera unos minutos e inténtalo de nuevo.'
                );
            } else {
                onMessage('error', msg || e?.message || 'Error al iniciar sesión');
            }
        } finally {
            this.setState({ disabled: false });
        }
    }
}

export default LoginController;
